import { useState, type FormEvent } from 'react'
import { ApiError, api, can, refusalReason, type Issue, type Vendor } from '../lib/api'
import { Status, TaskList } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { hashFor } from './vendor/sections'

// Statutory identity and banking, on their own tab, filled in by whoever has
// the numbers: finance, procurement, or the vendor. The register does not
// need them to assess a vendor; it needs them to activate one, and this tab
// says which are still owed and lets each be filled or waived with a reason.

const WAIVER_REASONS: [string, string][] = [
  ['', 'Does not apply because'],
  ['NON_RESIDENT', 'Non-resident entity'],
  ['GOVERNMENT_BODY', 'Government body'],
  ['GST_COMPOSITION', 'GST composition scheme'],
  ['GST_UNREGISTERED', 'Not registered for GST'],
  ['GST_EXEMPT_SUPPLY', 'Exempt supply'],
  ['SEZ', 'SEZ unit'],
  ['NOT_MSME', 'Not an MSME'],
  ['NO_TDS_APPLICABLE', 'No TDS applicable'],
  ['BELOW_LEI_THRESHOLD', 'Below the LEI threshold'],
  ['CONSTITUTION_NOT_APPLICABLE', 'Not applicable to this legal form'],
]

const GAP_LABEL: Record<string, string> = {
  constitution: 'Legal form',
  pan: 'PAN',
  gstin: 'GSTIN',
  cin: 'CIN',
  llpin: 'LLPIN',
  udyam_number: 'Udyam number',
  trc_on_file: 'Tax residency certificate',
  form_10f_on_file: 'Form 10F',
  no_pe_declaration: 'No permanent establishment declaration',
}

// Gaps that cannot be marked not applicable here. The legal form is recorded on
// the Profile, and the server keeps the gap whatever waiver is stored, so a
// "not applicable" choice for it would be a control that does nothing (review
// of 12 Oct, S4).
const NOT_WAIVABLE = new Set(['constitution'])

type Form = {
  pan: string
  cin: string
  llpin: string
  gstins: string
  msmeCategory: string
  udyamNumber: string
  ifsc: string
  accountNumber: string
  waivers: Record<string, string>
}

export function VendorStatutory({ vendor, onSaved }: { vendor: Vendor; onSaved: (v: Vendor, said: string) => void }) {
  const [f, setF] = useState<Form>({
    pan: vendor.pan ?? '',
    cin: '',
    llpin: '',
    gstins: '',
    msmeCategory: vendor.msmeCategory ?? '',
    udyamNumber: '',
    ifsc: '',
    accountNumber: '',
    waivers: {},
  })
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [general, setGeneral] = useState('')

  const gaps = vendor.statutory?.gaps ?? []
  const complete = vendor.statutory?.complete ?? true
  const isCompany = vendor.constitution === 'PRIVATE_LIMITED' || vendor.constitution === 'PUBLIC_LIMITED'
  const isLLP = vendor.constitution === 'LLP'
  const resident = vendor.residency !== 'NON_RESIDENT'
  const editable = can('vendor.edit')

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setF((cur) => ({ ...cur, [k]: v }))
  }

  async function save(ev: FormEvent) {
    ev.preventDefault()
    setBusy(true)
    setErrors({})
    setGeneral('')
    const changes: Record<string, unknown> = {}
    if (f.pan.trim() && f.pan.trim().toUpperCase() !== (vendor.pan ?? '')) changes.pan = f.pan.trim().toUpperCase()
    if (f.cin.trim()) changes.cin = f.cin.trim().toUpperCase()
    if (f.llpin.trim()) changes.llpin = f.llpin.trim().toUpperCase()
    if (f.gstins.trim()) changes.gstins = f.gstins.split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean)
    if (f.msmeCategory !== (vendor.msmeCategory ?? '')) changes.msmeCategory = f.msmeCategory
    if (f.udyamNumber.trim()) changes.udyamNumber = f.udyamNumber.trim().toUpperCase()
    if (f.ifsc.trim()) changes.ifsc = f.ifsc.trim().toUpperCase()
    if (f.accountNumber.trim()) changes.accountNumber = f.accountNumber.trim()
    const waivers = Object.fromEntries(Object.entries(f.waivers).filter(([, r]) => r))
    if (Object.keys(waivers).length) changes.waivers = waivers
    if (Object.keys(changes).length === 0) {
      setGeneral('Nothing to save yet, fill in a number or mark one not applicable')
      setBusy(false)
      return
    }
    try {
      await api.updateVendor(vendor.id, changes)
      const fresh = await api.vendor(vendor.id)
      onSaved(fresh, 'Saved. ' + (fresh.statutory?.complete ? 'The statutory identity is complete.' : (fresh.statutory?.gaps.length ?? 0) + ' still to go.'))
      setF((cur) => ({ ...cur, cin: '', llpin: '', gstins: '', udyamNumber: '', ifsc: '', accountNumber: '', waivers: {} }))
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) {
        const m: Record<string, string> = {}
        const rest: string[] = []
        for (const i of err.issues as Issue[]) {
          const key = FIELD[i.field] ?? i.field
          if (key in f) m[key] = i.detail
          else rest.push(i.detail)
        }
        setErrors(m)
        setGeneral(rest.join(' '))
      } else {
        setGeneral(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setBusy(false)
    }
  }

  const e = (k: string) => (errors[k] ? <p className="msg">{errors[k]}</p> : null)
  const cls = (k: string) => (errors[k] ? 'err' : '')

  return (
    <div className="grid-2">
      <section className="panel">
        <h2>
          Still owed for activation{' '}
          {complete ? <Status tone="ok">Complete</Status> : <Status tone="crit">{gaps.length} missing</Status>}
        </h2>
        {complete ? (
          <p className="hint">Every identifier this legal form needs is on file or waived</p>
        ) : (
          // The rule behind each gap is the API's sentence and sometimes runs
          // past a line, so it sits behind the (i) beside the gap it explains.
          <TaskList
            items={gaps.map((g) => {
              const name = GAP_LABEL[g.field] ?? g.field
              return {
                key: g.field,
                name,
                state: 'missing' as const,
                action:
                  g.field === 'constitution' ? (
                    <>
                      <a href={hashFor(vendor.id, { section: 'profile', part: null })}>Set it on the Profile</a>{' '}
                      <ReasonTip label={'What ' + name + ' needs'} text={g.detail} />
                    </>
                  ) : (
                    <ReasonTip label={'What ' + name + ' needs'} text={g.detail} />
                  ),
              }
            })}
          />
        )}
        <h2 style={{ marginTop: 16 }}>On file</h2>
        <dl className="kv">
          <dt>Legal form</dt>
          <dd>{pretty(vendor.constitution)}</dd>
          <dt>Residency</dt>
          <dd>{pretty(vendor.residency)}</dd>
          <dt>PAN</dt>
          <dd className="mono">{vendor.pan || <span className="muted">not on file</span>}</dd>
          <dt>MSME</dt>
          <dd>{vendor.msmeCategory ? pretty(vendor.msmeCategory) : <span className="muted">not declared</span>}</dd>
        </dl>
      </section>

      <form className="panel p1-form" onSubmit={save} noValidate>
        <div className="wide">
          <h2 style={{ marginTop: 0 }}>Add what you have</h2>
        </div>
        {resident && (
          <div>
            <label htmlFor="st-pan">PAN</label>
            <input id="st-pan" type="text" className={cls('pan')} value={f.pan} maxLength={10} placeholder="AAACH7409R" disabled={!editable} onChange={(ev) => set('pan', ev.target.value)} />
            {e('pan')}
          </div>
        )}
        {isCompany && (
          <div>
            <label htmlFor="st-cin">CIN</label>
            <input id="st-cin" type="text" className={cls('cin')} value={f.cin} maxLength={21} placeholder="U72900KA2015PTC080000" disabled={!editable} onChange={(ev) => set('cin', ev.target.value)} />
            {e('cin')}
          </div>
        )}
        {isLLP && (
          <div>
            <label htmlFor="st-llpin">LLPIN</label>
            <input id="st-llpin" type="text" className={cls('llpin')} value={f.llpin} maxLength={8} placeholder="AAA-1234" disabled={!editable} onChange={(ev) => set('llpin', ev.target.value)} />
            {e('llpin')}
          </div>
        )}
        {resident && (
          <div className="wide">
            <label htmlFor="st-gstin">
              GSTIN <span className="opt">(one per state, separated by commas)</span>
            </label>
            <input id="st-gstin" type="text" className={cls('gstins')} value={f.gstins} placeholder="29AAACS1234F1Z5" disabled={!editable} onChange={(ev) => set('gstins', ev.target.value)} />
            {e('gstins')}
            {e('gstin')}
          </div>
        )}
        <div>
          <label htmlFor="st-msme">MSME category</label>
          <select id="st-msme" value={f.msmeCategory} disabled={!editable} onChange={(ev) => set('msmeCategory', ev.target.value)}>
            <option value="">Not an MSME, or not declared</option>
            <option value="MICRO">Micro</option>
            <option value="SMALL">Small</option>
            <option value="MEDIUM">Medium</option>
          </select>
        </div>
        <div>
          <label htmlFor="st-udyam">Udyam number</label>
          <input id="st-udyam" type="text" className={cls('udyamNumber')} value={f.udyamNumber} placeholder="UDYAM-KA-03-0123456" disabled={!editable} onChange={(ev) => set('udyamNumber', ev.target.value)} />
          {e('udyamNumber')}
          {e('udyam_number')}
        </div>
        <div>
          <label htmlFor="st-ifsc">
            IFSC <span className="opt">(optional)</span>
          </label>
          <input id="st-ifsc" type="text" className={cls('ifsc')} value={f.ifsc} maxLength={11} placeholder="HDFC0001234" disabled={!editable} onChange={(ev) => set('ifsc', ev.target.value)} />
          {e('ifsc')}
        </div>
        <div>
          <label htmlFor="st-acct">
            Account number <span className="opt">(optional)</span>
          </label>
          <input id="st-acct" type="text" className={cls('accountNumber')} value={f.accountNumber} disabled={!editable} onChange={(ev) => set('accountNumber', ev.target.value)} />
          {e('accountNumber')}
          {e('account_number')}
        </div>

        {gaps.some((g) => !NOT_WAIVABLE.has(g.field)) && (
          <div className="wide">
            <label>Mark as not applicable</label>
            <p className="muted" style={{ margin: '0 0 6px' }}>
              A reason is recorded against the vendor, nobody types NA
            </p>
            {gaps.filter((g) => !NOT_WAIVABLE.has(g.field)).map((g) => (
              <div key={g.field} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <span style={{ minWidth: 160 }}>{GAP_LABEL[g.field] ?? g.field}</span>
                <select value={f.waivers[g.field] ?? ''} disabled={!editable} onChange={(ev) => set('waivers', { ...f.waivers, [g.field]: ev.target.value })}>
                  {WAIVER_REASONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        {general && (
          <div className="wide note" role="status">
            {general}
          </div>
        )}
        <div className="p1-form-foot">
          <button
            type="submit"
            className="primary"
            disabled={busy || !editable}
            data-action="tprm.vendor,vendor-record.a25"
          >
            {busy ? 'Saving' : 'Save'}
          </button>
          {!editable && (
            <span className="muted">
              <Status tone="neutral">Read only</Status>{' '}
              <ReasonTip label="Why this is read only" text={refusalReason('vendor.edit')} />
            </span>
          )}
        </div>
      </form>
    </div>
  )
}

const FIELD: Record<string, string> = {
  pan: 'pan',
  cin: 'cin',
  llpin: 'llpin',
  gstin: 'gstins',
  gstins: 'gstins',
  udyam_number: 'udyamNumber',
  udyamNumber: 'udyamNumber',
  msmeCategory: 'msmeCategory',
  ifsc: 'ifsc',
  account_number: 'accountNumber',
  accountNumber: 'accountNumber',
}

function pretty(v: string | undefined | null): string {
  if (!v) return 'not recorded'
  return v.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}
